import {
  createSlice,
  createSelector,
  createAsyncThunk,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import { queryStringFromArray } from "helpers";
import { uncoveryApi } from "../../../services";

const inputAdapter = createEntityAdapter();
const initialState = inputAdapter.getInitialState({
  fetchAllInputsStatus: "idle",
  archiveAssetStatus: "idle",
  archiveAssetErrorMessage: "",
  hasNextPage: false,
  page: 1,
  pageSize: 50,
  sortBy: "createdAt",
  orderBy: "desc",
  searchTerm: "",
  count: 0,
  errorMessage: "",
  fetchAllInputsErrorMessage: "",
  status: "idle",
  values: [],
  date: "",
  isCheckedAll: false,
});

const options = {
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
};

export const fetchAllInputs = createAsyncThunk(
  "inputs/fetchAllInputs",
  async ({ entityId }, { rejectWithValue, getState }) => {
    const { page, pageSize, sortBy, orderBy, searchTerm } = getState().input;
    const query = `/entities/${entityId}/inputs?page=${page}&pageSize=${pageSize}&sortBy=${sortBy}&orderBy=${orderBy}&searchTerm=${searchTerm}`;
    try {
      const response = await uncoveryApi.get(query);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const addInput = createAsyncThunk(
  "inputs/add",
  async ({ inputs, entityId }, thunkAPI) => {
    try {
      const response = await uncoveryApi.post(
        `/entities/${entityId}/inputs`,
        {
          inputs,
        },
        options
      );
      if (response.status === 201) {
        return response.data;
      } else {
        return thunkAPI.rejectWithValue(response.data);
      }
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const archiveAsset = createAsyncThunk(
  "input/archiveAsset",
  async ({ entityId, assetIds }, { rejectWithValue }) => {
    const qsAssetIds = queryStringFromArray(assetIds, "assetId");
    const query = `/entities/${entityId}/assets?${qsAssetIds}`;
    const body = {
      archived: true,
    };
    try {
      const response = await uncoveryApi.put(query, body);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const inputSlice = createSlice({
  name: "input",
  initialState,
  reducers: {
    updateSearchTerm: (state, { payload }) => {
      const { searchTerm } = payload;
      state.page = 1;
      state.searchTerm = searchTerm;
      return state;
    },
    sort: (state, { payload }) => {
      const { sortBy } = payload;
      state.page = 1;
      if (sortBy !== state.sortBy) {
        state.orderBy = "asc";
        state.sortBy = sortBy;
      } else {
        state.orderBy = state.orderBy === "desc" ? "asc" : "desc";
      }
      return state;
    },
    updatePage: (state, { payload }) => {
      const { page } = payload;
      state.page = page;
      return state;
    },
    setScrollPosition: (state, { payload }) => {
      const { scrollPosition } = payload;
      state.scrollPosition = scrollPosition;
      return state;
    },
    refreshInputStatus: (state) => {
      state.status = "idle";
      return state;
    },
    checkValue: (state, { payload }) => {
      const { assetId } = payload;
      const index = state.values.findIndex((e) => e.id === assetId);
      state.values[index].isChecked = !state.values[index].isChecked;
      return state;
    },
    checkAllValues: (state, { payload }) => {
      const { isChecked } = payload;
      state.isCheckedAll = isChecked;
      state.values.forEach((e) => (e.isChecked = isChecked));
      return state;
    },
    resetInputState: () => initialState,
  },
  extraReducers: {
    [fetchAllInputs.pending]: (state, { payload }) => {
      state.fetchAllInputsStatus = "loading";
    },
    [fetchAllInputs.fulfilled]: (state, { payload }) => {
      payload.data.forEach((e) => {
        e.isChecked = false;
      });
      if (state.page === 1) {
        state.values = payload.data;
      } else {
        state.values = [...state.values, ...payload.data];
      }
      state.hasNextPage = payload.pageInfo.hasNextPage;
      state.count = payload.pageInfo.count;
      state.fetchAllInputsStatus = "success";
    },
    [fetchAllInputs.rejected]: (state, { payload }) => {
      state.fetchAllInputsErrorMessage = payload.message;
      state.fetchAllInputsStatus = "error";
    },
    [addInput.pending]: (state, { payload }) => {
      state.status = "loading";
    },
    [addInput.fulfilled]: (state, { payload }) => {
      state.status = "success";
    },
    [addInput.rejected]: (state, { payload }) => {
      state.errorMessage = payload.message;
      state.status = "error";
    },
    [archiveAsset.pending]: (state) => {
      state.archiveAssetStatus = "loading";
    },
    [archiveAsset.fulfilled]: (state) => {
      state.isCheckedAll = false;
      state.values.forEach((e) => (e.isChecked = false));
      state.archiveAssetStatus = "success";
    },
    [archiveAsset.rejected]: (state, { payload }) => {
      state.archiveAssetErrorMessage = payload.message;
      state.archiveAssetStatus = "error";
    },
  },
});

export const {
  refreshInputStatus,
  updateSearchTerm,
  sort,
  updatePage,
  setScrollPosition,
  checkValue,
  checkAllValues,
  resetInputState,
} = inputSlice.actions;

export default inputSlice.reducer;

const inputState = (state) => state.input;
// const getId = (_, id) => id;
export const selectInputIds = createSelector(inputState, (asset) => {
  return asset.values.map((v) => v.id);
});
export const selectInputs = (state) => state.input.values;
export const selectInputById = (state, assetId) => {
  return state.input.values.find((v) => v.id === assetId);
};

// export const {
//   selectAll: selectInputs,
//   selectById: selectInputById,
//   selectIds: selectInputIds,
// } = inputAdapter.getSelectors((state) => state.input);
